import type { ObjectTypeGroup } from '@/entities/Inventory/ObjectSubTypes'
import type { ObjectInventoryField } from '@/entities/Inventory/ObjectTypes'
import type { GridFieldType } from '@/features/Inventory/ObjectTypePassportGrid/model/types'
import { isFieldMandatory } from './validateMandatoryFields'

const GENERAL_TAB_ID = 'general'

type RuntimePlaceholderKind = 'missing-field-metadata' | 'unsupported-item' | 'unsupported-section'

export type RuntimeFieldSlot = {
  kind: 'field'
  fieldId: string
  layoutItem: GridFieldType
  field: ObjectInventoryField
}

export type RuntimePlaceholderItem = {
  kind: 'placeholder'
  placeholderKind: Exclude<RuntimePlaceholderKind, 'unsupported-section'>
  layoutItem: GridFieldType
}

export type RuntimeLayoutItem = RuntimeFieldSlot | RuntimePlaceholderItem

/** Matches passport builder v2: blue row bars vs amber list/table blocks. */
export type RuntimeSectionHeadingVariant = 'blue' | 'amber' | 'muted'

export type RuntimeLayoutSection = {
  id: string
  kind: 'row' | 'placeholder'
  row: number | null
  items: RuntimeLayoutItem[]
  placeholderKind?: RuntimePlaceholderKind
  headingVariant: RuntimeSectionHeadingVariant
  headingLabel: string
}

export type RuntimeLayoutTab = {
  id: string
  label: string
  groupId?: number
  sections: RuntimeLayoutSection[]
}

export type RuntimeLayout = {
  isValid: boolean
  tabs: RuntimeLayoutTab[]
}

export type BuildRuntimeLayoutInput = {
  passport_grid_template?: GridFieldType[] | null
  field_groups?: ObjectTypeGroup[] | null
  fieldMetadata?: ObjectInventoryField[] | null
}

type RuntimeTabDraft = {
  id: string
  label: string
  groupId?: number
  order: number
  items: GridFieldType[]
}

export function buildRuntimeLayout(input: BuildRuntimeLayoutInput): RuntimeLayout {
  const tabs = buildRuntimeTabs(input)
  return {
    isValid: Boolean((input.passport_grid_template ?? []).length),
    tabs,
  }
}

export function isRuntimeLayoutValid(layout: RuntimeLayout): boolean {
  return layout.isValid
}

export function buildRuntimeTabs(input: BuildRuntimeLayoutInput): RuntimeLayoutTab[] {
  const template = input.passport_grid_template ?? []
  if (!template.length) return []

  const groups = groupMap(input.field_groups)
  const metadata = metadataMap(input.fieldMetadata)
  return tabDrafts(template, groups).map((draft) => buildTab(draft, groups, metadata))
}

function groupMap(groups?: ObjectTypeGroup[] | null): Map<number, ObjectTypeGroup> {
  return new Map((groups ?? []).map((group) => [group.id, group]))
}

function metadataMap(fields?: ObjectInventoryField[] | null): Map<string, ObjectInventoryField> {
  return new Map((fields ?? []).map((field) => [String(field.id), field]))
}

/**
 * Grid cells from «Справочники» use a composite `id` while API values/metadata use the leaf inventory
 * field id (`nested_binding.leaf_field_id` or `#f:` suffix) — same rules as `PassportBuilderGridRow.fieldMeta`.
 */
function parseCompositeLeafFieldId(gridCellId: string): string | null {
  const hashIdx = gridCellId.lastIndexOf('#f:')
  if (hashIdx >= 0) return gridCellId.slice(hashIdx + 3)
  return null
}

/**
 * Extracts the owning (parent) field ID from a composite grid cell reference.
 */
function parseCompositeOwnerFieldId(gridCellId: string): string | null {
  const m = gridCellId.match(/^ref:\[\d+:([A-Za-z0-9]+):\d+]/)
  if (m?.[1]) return m[1]
  return null
}

/**
 * Id ячейки вида `parent:<fieldId>:<suffix>` / `element:<fieldId>:<suffix>`.
 * Суффикс в билдере — `crypto.randomUUID()` (не только число).
 */
export function parsePrefixedPaletteFieldId(gridCellId: string): string | null {
  const m = String(gridCellId).match(/^(?:parent|element):([^:]+):.+$/)
  const id = m?.[1]?.trim()
  return id || null
}

/**
 * Builder «Справочники» parent-accordion drops persist composite ids, e.g.
 * `parent-acc-116-<fieldId>-root::key::…` or `parent-acc-84-<edge>-root::<leafId>::…`.
 */
function parseParentAccordionGridFieldId(gridId: string): string | null {
  const keyMatch = gridId.match(/^parent-acc-\d+-([A-Za-z0-9]+)-root::key::/)
  if (keyMatch?.[1]) return keyMatch[1]

  const leafMatch = gridId.match(/^parent-acc-\d+-[A-Za-z0-9]+-root::([A-Za-z0-9]+)::/)
  if (leafMatch?.[1] && leafMatch[1] !== 'key') return leafMatch[1]

  return null
}

export function resolveRuntimeFieldMetadataForGridItem(
  item: GridFieldType,
  fieldMetadata?: ObjectInventoryField[] | null,
): ObjectInventoryField | undefined {
  return resolveRuntimeFieldForGridItem(item, metadataMap(fieldMetadata))
}

function resolveRuntimeFieldForGridItem(
  item: GridFieldType,
  metadata: Map<string, ObjectInventoryField>,
): ObjectInventoryField | undefined {
  const lookup = (id: string): ObjectInventoryField | undefined => metadata.get(id)

  const sid = String(item.id)
  const direct = lookup(sid)
  if (direct) return direct

  const leafFromNested = item.nested_binding?.leaf_field_id
  if (leafFromNested != null) {
    const fromLeaf = lookup(String(leafFromNested))
    if (fromLeaf) return fromLeaf
  }

  const parsedLeaf = parseCompositeLeafFieldId(sid)
  if (parsedLeaf) {
    const fromParsed = lookup(String(parsedLeaf))
    if (fromParsed) return fromParsed
  }

  const parentAccLeaf = parseParentAccordionGridFieldId(sid)
  if (parentAccLeaf) {
    const fromParentAcc = lookup(String(parentAccLeaf))
    if (fromParentAcc) return fromParentAcc
  }

  const prefixed = parsePrefixedPaletteFieldId(sid)
  if (prefixed) {
    const fromPrefixed = lookup(prefixed)
    if (fromPrefixed) return fromPrefixed
  }

  const inv = (item as GridFieldType & { inventory_field_id?: string | number }).inventory_field_id
  if (inv != null) {
    return lookup(String(inv))
  }

  return undefined
}

function emptyGroupTabDrafts(
  groups: Map<number, ObjectTypeGroup>,
  existing: Map<string, RuntimeTabDraft>,
): RuntimeTabDraft[] {
  const out: RuntimeTabDraft[] = []
  let seq = existing.size
  for (const [, group] of groups) {
    if (!isRuntimeTabGroup(group)) continue
    const id = `group:${group.id}`
    if (existing.has(id)) continue
    out.push({
      id,
      label: group.name?.trim() ? group.name : `Group ${group.id}`,
      groupId: group.id,
      order: seq++,
      items: [],
    })
  }
  return out
}

function tabDrafts(
  template: GridFieldType[],
  groups: Map<number, ObjectTypeGroup>,
): RuntimeTabDraft[] {
  const drafts = new Map<string, RuntimeTabDraft>()
  for (const item of template) {
    const draft = draftForItem(item, groups, drafts, drafts.size)
    draft.items.push(item)
  }
  for (const draft of emptyGroupTabDrafts(groups, drafts)) {
    drafts.set(draft.id, draft)
  }
  return sortDrafts([...drafts.values()], groups)
}

function draftForItem(
  item: GridFieldType,
  groups: Map<number, ObjectTypeGroup>,
  drafts: Map<string, RuntimeTabDraft>,
  order: number,
): RuntimeTabDraft {
  const key = tabKey(item, groups)
  const existing = drafts.get(key)
  if (existing) return existing
  const created = createDraft(item, groups, order)
  drafts.set(key, created)
  return created
}

function createDraft(
  item: GridFieldType,
  groups: Map<number, ObjectTypeGroup>,
  order: number,
): RuntimeTabDraft {
  const groupId = runtimeTabGroupId(item, groups)
  if (groupId == null) {
    return { id: GENERAL_TAB_ID, label: GENERAL_TAB_ID, order, items: [] }
  }
  const group = groups.get(groupId)
  return {
    id: `group:${groupId}`,
    label: group?.name || `Group ${groupId}`,
    groupId,
    order,
    items: [],
  }
}

function buildTab(
  draft: RuntimeTabDraft,
  groups: Map<number, ObjectTypeGroup>,
  metadata: Map<string, ObjectInventoryField>,
): RuntimeLayoutTab {
  const section = unsupportedSection(draft, groups)
  if (section) {
    return { id: draft.id, label: draft.label, groupId: draft.groupId, sections: [section] }
  }
  let sections = rowSections(draft, groups, metadata)
  if (!sections.length && draft.groupId != null && isRuntimeTabGroup(groups.get(draft.groupId))) {
    const group = groups.get(draft.groupId)
    sections = [
      {
        id: `${draft.id}:empty`,
        kind: 'row',
        row: 0,
        headingVariant: 'muted',
        headingLabel: group?.name?.trim() ? group.name : draft.label,
        items: [],
      },
    ]
  }
  return {
    id: draft.id,
    label: draft.label,
    groupId: draft.groupId,
    sections,
  }
}

function unsupportedSection(
  draft: RuntimeTabDraft,
  groups: Map<number, ObjectTypeGroup>,
): RuntimeLayoutSection | null {
  if (draft.groupId == null) return null
  const group = groups.get(draft.groupId)
  const type = group?.type
  if (type === 'GROUP' || type == null) return null
  const headingVariant: RuntimeSectionHeadingVariant = type === 'LIST' ? 'amber' : 'muted'
  const headingLabel = group?.name?.trim() ? group.name : draft.label
  return {
    id: `${draft.id}:placeholder`,
    kind: 'placeholder',
    row: null,
    items: [],
    placeholderKind: 'unsupported-section',
    headingVariant,
    headingLabel,
  }
}

function rowSections(
  draft: RuntimeTabDraft,
  groups: Map<number, ObjectTypeGroup>,
  metadata: Map<string, ObjectInventoryField>,
): RuntimeLayoutSection[] {
  const sections = sectionDrafts(draft.items, draft.id, groups)
  return sections.map((section) => buildSection(section, groups, metadata))
}

type RuntimeSectionDraft = {
  id: string
  kind: 'row' | 'placeholder'
  row: number
  items: GridFieldType[]
}

function sectionDrafts(
  items: GridFieldType[],
  tabId: string,
  groups: Map<number, ObjectTypeGroup>,
): RuntimeSectionDraft[] {
  const drafts = new Map<string, RuntimeSectionDraft>()
  for (const item of items) {
    const key = sectionKey(item, tabId, groups)
    const existing = drafts.get(key)
    if (existing) {
      existing.items.push(item)
      continue
    }
    drafts.set(key, {
      id: key,
      kind: isUnsupportedSectionItem(item, groups) ? 'placeholder' : 'row',
      row: item.row,
      items: [item],
    })
  }
  return [...drafts.values()].sort((left, right) => left.row - right.row)
}

function fallbackRowHeadingLabel(row: number): string {
  return `Строка ${row + 1}`
}

function headingForGridSection(
  section: RuntimeSectionDraft,
  groups: Map<number, ObjectTypeGroup>,
): { headingVariant: RuntimeSectionHeadingVariant; headingLabel: string } {
  if (section.kind === 'placeholder') {
    const item = section.items[0]
    const gid = item ? normalizedGroupId(item) : null
    const group = gid != null ? groups.get(gid) : undefined
    const isList = group?.type === 'LIST'
    return {
      headingVariant: isList ? 'amber' : 'muted',
      headingLabel: group?.name?.trim() ? group.name : fallbackRowHeadingLabel(section.row),
    }
  }

  const gids = new Set<number>()
  for (const it of section.items) {
    const gid = normalizedGroupId(it)
    if (gid != null) gids.add(gid)
  }

  if (gids.size === 1) {
    const gid = [...gids][0]!
    const group = groups.get(gid)
    const label = group?.name?.trim() ? group.name : fallbackRowHeadingLabel(section.row)
    const variant: RuntimeSectionHeadingVariant = group?.type === 'LIST' ? 'amber' : 'blue'
    return { headingVariant: variant, headingLabel: label }
  }

  return {
    headingVariant: 'blue',
    headingLabel: fallbackRowHeadingLabel(section.row),
  }
}

function buildSection(
  section: RuntimeSectionDraft,
  groups: Map<number, ObjectTypeGroup>,
  metadata: Map<string, ObjectInventoryField>,
): RuntimeLayoutSection {
  const heading = headingForGridSection(section, groups)
  if (section.kind === 'placeholder') {
    return {
      id: section.id,
      kind: 'placeholder',
      row: section.row,
      items: [],
      placeholderKind: 'unsupported-section',
      headingVariant: heading.headingVariant,
      headingLabel: heading.headingLabel,
    }
  }
  return {
    id: section.id,
    kind: 'row',
    row: section.row,
    headingVariant: heading.headingVariant,
    headingLabel: heading.headingLabel,
    items: [...section.items]
      .sort(
        (left, right) => left.cell - right.cell || String(left.id).localeCompare(String(right.id)),
      )
      .map((item) => runtimeItem(item, metadata)),
  }
}

/**
 * For composite grid references (e.g. `ref:[type:ownerField:sub]#f:leaf`), the resolved leaf field
 * may lack `mandatory` even though the owning field is mandatory. Propagate the flag so runtime
 * labels, validation and highlights work correctly.
 */
function propagateOwnerMandatory(
  item: GridFieldType,
  resolved: ObjectInventoryField,
  metadata: Map<string, ObjectInventoryField>,
): ObjectInventoryField {
  if (isFieldMandatory(resolved.mandatory)) return resolved
  const sid = String(item.id ?? '')
  const ownerId = parseCompositeOwnerFieldId(sid)
  if (!ownerId) return resolved
  const owner = metadata.get(ownerId)
  if (owner && isFieldMandatory(owner.mandatory)) {
    return { ...resolved, mandatory: true }
  }
  return resolved
}

function runtimeItem(
  item: GridFieldType,
  metadata: Map<string, ObjectInventoryField>,
): RuntimeLayoutItem {
  if (!isRuntimeField(item)) {
    return {
      kind: 'placeholder',
      placeholderKind: 'unsupported-item',
      layoutItem: item,
    }
  }
  const field = resolveRuntimeFieldForGridItem(item, metadata)
  if (!field) {
    if (item.palette_source === 'element') {
      return elementLinkedFieldSlot(item)
    }
    console.warn(
      '[passport-runtime-v2] unresolved field metadata for grid item',
      item.id,
      item.name,
    )
    return {
      kind: 'placeholder',
      placeholderKind: 'missing-field-metadata',
      layoutItem: item,
    }
  }
  const resolved = propagateOwnerMandatory(item, field, metadata)
  const inventoryFieldId = item.inventory_field_id != null ? String(item.inventory_field_id) : null
  const fieldId = inventoryFieldId ?? String(resolved.id)
  const canonicalField = fieldId === String(resolved.id) ? resolved : { ...resolved, id: fieldId }
  return {
    kind: 'field',
    fieldId,
    layoutItem: item,
    field: canonicalField,
  }
}

/** Synthetic field slot for element-linked grid items whose leaf field is not in metadata. */
function elementLinkedFieldSlot(item: GridFieldType): RuntimeFieldSlot {
  const leafId = parseCompositeLeafFieldId(String(item.id ?? ''))
  const fieldId = leafId ?? String(item.id)
  const synthetic: ObjectInventoryField = {
    id: fieldId,
    name: item.name ?? '',
    type: 'string',
    items: [],
    default_value: '',
    object_type: 0,
    multiple: false,
    count_object: false,
    is_filtered: false,
    is_table: false,
    is_system: false,
    is_global_search: false,
  }
  return {
    kind: 'field',
    fieldId: synthetic.id,
    layoutItem: item,
    field: synthetic,
  }
}

function isRuntimeField(item: GridFieldType): boolean {
  return item.type == null || item.type === 'field'
}

function tabKey(item: GridFieldType, groups: Map<number, ObjectTypeGroup>): string {
  const groupId = runtimeTabGroupId(item, groups)
  return groupId == null ? GENERAL_TAB_ID : `group:${groupId}`
}

function normalizedGroupId(item: GridFieldType): number | null {
  const groupId = item.group_id
  return groupId != null && Number(groupId) > 0 ? Number(groupId) : null
}

function sortDrafts(
  drafts: RuntimeTabDraft[],
  groups: Map<number, ObjectTypeGroup>,
): RuntimeTabDraft[] {
  const general = drafts.find((draft) => draft.id === GENERAL_TAB_ID)
  const rest = drafts
    .filter((draft) => draft.id !== GENERAL_TAB_ID)
    .sort((left, right) => compareTabDrafts(left, right, groups))
  return general ? [general, ...rest] : rest
}

function runtimeTabGroupId(
  item: GridFieldType,
  groups: Map<number, ObjectTypeGroup>,
): number | null {
  const groupId = normalizedGroupId(item)
  if (groupId == null) return null
  return isRuntimeTabGroup(groups.get(groupId)) ? groupId : null
}

function isRuntimeTabGroup(group?: ObjectTypeGroup): boolean {
  return Boolean(group?.is_tab && group.type === 'GROUP')
}

function isUnsupportedSectionItem(
  item: GridFieldType,
  groups: Map<number, ObjectTypeGroup>,
): boolean {
  const groupId = normalizedGroupId(item)
  if (groupId == null || runtimeTabGroupId(item, groups) != null) return false
  const group = groups.get(groupId)
  return group != null && group.type !== 'GROUP'
}

function sectionKey(
  item: GridFieldType,
  tabId: string,
  groups: Map<number, ObjectTypeGroup>,
): string {
  const groupId = normalizedGroupId(item)
  if (groupId != null && keepsOwnGeneralSection(item, groups)) {
    return `${tabId}:group:${groupId}:row:${item.row}`
  }
  return `${tabId}:row:${item.row}`
}

function keepsOwnGeneralSection(
  item: GridFieldType,
  groups: Map<number, ObjectTypeGroup>,
): boolean {
  const groupId = normalizedGroupId(item)
  if (groupId == null || runtimeTabGroupId(item, groups) != null) return false
  return groups.has(groupId)
}

function compareTabDrafts(
  left: RuntimeTabDraft,
  right: RuntimeTabDraft,
  groups: Map<number, ObjectTypeGroup>,
): number {
  const leftSort = groupSort(left, groups)
  const rightSort = groupSort(right, groups)
  if (leftSort !== rightSort) return leftSort - rightSort
  return left.order - right.order
}

function groupSort(draft: RuntimeTabDraft, groups: Map<number, ObjectTypeGroup>): number {
  if (draft.groupId == null) return -1
  return groups.get(draft.groupId)?.sort ?? Number.MAX_SAFE_INTEGER
}
